import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import gui_definition_types = api_configuration.libraries.gui_definition_lib.types;
import { hasMutationPermission } from "../state 2 interface/permissions";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { setLanguage } from "../front/translate";
import { DecoratedSchemas } from "./schema";
import DestructorTree from "../DestructorTree";
import { createEmptyRemoteNode } from "./addClientState";
import { nodeValid } from "../state/subscribeToNodePropertyValid";
import { nodeModified /*, anyChangesOnNode*/ } from "../state/subscribeToNodePropertyChange";
import { asRegisterable, getCollectionRegistrations } from "../state/registerClientState";
import { applyDefaultsWithUpdates, applyingDefaults } from "../state/defaults";
import { resolvingReferences } from "../state 2 interface/referenceResolver";
import { loadControls } from "../widgets/ControlLibrary";
import { getQueryEntryIndex } from "../state/Query";
import { ObservableNumber } from "../observableTypes";
import { pendingSubscriptions } from "../state 2 interface/SubscriptionManager";
import { iText } from "../state/Text";
import { iStategroup } from "../state/Stategroup";
import { iCollection } from "../state/Collection";
import { createVariable } from "../../../../../stream";
import { DatastoreProxy } from "../interface/DatastoreProxy";

export let client_state: api_configuration.types.client_state.alan_api.Cclient_state;
export let main: gui_definition_types.main.alan_api.Cmain;
export let gui_model: gui_definition_types.gui_model.alan_api.Cgui_model;
export let settings: gui_definition_types.settings.alan_api.Csettings;
export let client_schemas: DecoratedSchemas;

export const UNKNOWN_CLIENT_VERSION = "not yet determined";
// eslint-disable-next-line prefer-const
export let engine_version = UNKNOWN_CLIENT_VERSION;

export function getApplicationName(): string {
	if (settings && client_state) {
		const application_name_phrase = settings.properties.settings.properties.application_name.entry;
		return client_state.properties.selected_language.ref.key.ref.properties.translations.get(application_name_phrase)!
			.properties.translation;
	}
	return "";
}

export function initialize(schemas: DecoratedSchemas): void {
	client_schemas = schemas;
	main = schemas.main;
	gui_model = schemas.gui_model;
	settings = schemas.settings;
	loadControls(schemas.controls);

	client_state = api_configuration.types.client_state.alan_api.Cclient_state.create(
		{
			root: createEmptyRemoteNode(gui_model.properties.root.input.application_node()),
			"is editable": ["no", {}],
			session: {
				status: ["not authenticated", {}],
			},
			languages: Object.keys(schemas.translations).reduce<Record<string, any>>(function (l, t) {
				l[t] = {
					label: schemas.translations[t].properties.language,
				};
				return l;
			}, {}),
			"selected language": main.input.settings.properties.settings.properties.default_language.entry,
			messages: {},
			"has landing page": "no",
			"server connection status": ["alive", {}],
			"network connection status": ["alive", {}],
			"platform family": ["undetermined", {}],
			"browser family": ["undetermined", {}],
			version: "",
			"alt key pressed": ["no", {}],
			"ctrl key pressed": ["no", {}],
			"shift key pressed": ["no", {}],
			"meta key pressed": ["no", {}],
			"toggle key pressed": ["no", {}],
			"logo url": "engine/static/logo.png",
			"logo-sideways url": "engine/static/logo-sideways.png",
			"report types": Object.keys(schemas.reports).reduce<api_cs.Tclient_state["report types"]>((types, report) => {
				types[report] = {};
				return types;
			}, {}),
			modules: {},
			reports: {},
			windows: {},
		},
		{
			client_bindings: schemas.client_bindings,
			gui_model: gui_model,
			application: gui_model.input.application,
			languages: schemas.translations,
			settings: settings,
			views: schemas.views,
			widget: schemas.widgets,
			reports: schemas.reports,
		}
	);
	client_state.subscribe_selected_language({
		on_update: () => {
			client_state.properties.selected_language.ref.key.ref.properties.engine_language.switch({
				dutch: () => setLanguage("dutch"),
				english: () => setLanguage("english"),
			});
		},
	});
	client_state.properties.session.properties.status.subscribe({
		on_update: function () {
			client_state.properties.session.properties.status.switch({
				authenticated: function () {
					hasMutationPermission(IdPathBuilder.create(), (has_permission) => {
						if (has_permission) {
							client_state.properties.is_editable.set("yes", {});
						} else {
							client_state.properties.is_editable.set("no", {});
						}
					});
				},
				"not authenticated": undefined,
			});
		},
	});
	nodeValid(client_state.properties.root).subscribe({ update: () => undefined });
	nodeModified(client_state.properties.root).subscribe({ update: () => undefined });
	// anyChangesOnNode(client_state.properties.root).subscribe({
	// 	update: () => {
	// 		//TODO edg: add and update a client state property
	// 	},
	// });
	applyDefaultsWithUpdates(client_state.properties.root, new DestructorTree());
	callInitializedHandlers();
}
let initialized = false;
const initializedCallbacks = new Set<() => void>();
export function onIntialize(callback: () => void): void {
	if (initialized) {
		callback();
	} else {
		initializedCallbacks.add(callback);
	}
}
function callInitializedHandlers() {
	initialized = true;
	for (const callback of initializedCallbacks) {
		callback();
	}
	initializedCallbacks.clear();
}

export function getUserNameOrDefault(): string | undefined {
	switch (client_state.properties.session.properties.status.state.name) {
		case "authenticated": {
			const $ = client_state.properties.session.properties.status.state.node;
			switch ($.properties.anonymous_session.state.name) {
				case "no": {
					const not_anonymous = $.properties.anonymous_session.state.node;
					return not_anonymous.properties.username;
				}
				case "yes":
					break;
				default:
					unexpectedState($.properties.anonymous_session.state);
			}
			break;
		}
		case "not authenticated":
			break;
		default:
			unexpectedState(client_state.properties.session.properties.status.state);
	}
	return undefined;
}

const transaction_requests = new ObservableNumber(0);
export function transact(): api_cs.Subscription {
	transaction_requests.increment();
	return {
		unsubscribe: () => transaction_requests.decrement(),
	};
}

export function onTransactionEnds(callback: () => void) {
	transaction_requests.subscribeConditional(
		(requests) => requests === 0,
		(ended, subscription) => {
			if (ended) {
				subscription.unsubscribe();
				callback();
			}
		}
	);
}

const MAXINSTRUCTIONS = 10;
let next_instruction_idx = 0;
const last_instructions: {
	instruction: string;
	date: number;
}[] = [];
export function logInstruction(context: api_cs.AlanObject, instruction_name: string): void {
	const path = context.path;
	last_instructions[next_instruction_idx] = {
		instruction: `${path}:${instruction_name}()`,
		date: Date.now(),
	};
	next_instruction_idx = (next_instruction_idx + 1) % MAXINSTRUCTIONS;
}
export function listLastInstructions(): string[] {
	const res = [];
	for (let i = 0; i < MAXINSTRUCTIONS; i++) {
		const last_instruction = last_instructions[(i + next_instruction_idx) % MAXINSTRUCTIONS];
		if (last_instruction !== undefined) {
			res.push(new Date(last_instruction.date).toISOString() + ": " + last_instruction.instruction);
		}
	}
	return res;
}

export const ignoreCollectionDefaults = createVariable(false, true);

/// Debugging reports
function nodeStatus(node: api_cs.Cnode): string {
	const valid = node.properties.is_valid.switch({
		no: "🔴",
		yes: "",
	});
	const modified = node.properties.modified.switch({
		yes: "🔵",
		no: "",
	});
	return `${valid}${modified}`;
}

function generateNodeReport(node: api_cs.Cnode): any {
	const report: any = {};
	const attributes: any = {};
	let has_attributes = false;
	let has_queries = false;
	let child_entries = 0;
	let query_entries = 0;
	for (const [attribute_name, attribute] of node.properties.attributes) {
		has_attributes = true;
		let obj: any = {};
		let status: any = [];
		let icons = "";
		switch (attribute.properties.type.state.name) {
			case "operation": {
				const operation = attribute.properties.type.state.node;
				switch (operation.properties.type.state.name) {
					case "action": {
						const action = operation.properties.type.state.node;
						const registration = asRegisterable(action);
						const refcount = registration.__references?.value ?? "-";
						status.push(refcount);
						break;
					}
					case "command": {
						const command = operation.properties.type.state.node;
						const registration = asRegisterable(command);
						const refcount = registration.__references?.value ?? "-";
						const subcount = registration.__subscriptions?.value ?? "-";
						status.push(`${refcount}/${subcount}`);
						break;
					}
				}
				operation.properties.started.switch({
					no: () => {
						status.push("not started");
					},
					yes: ($) => {
						icons += nodeStatus($.properties.node);
						obj = generateNodeReport($.properties.node);
					},
				});
				break;
			}
			case "property": {
				const property = attribute.properties.type.state.node;
				switch (property.properties.type.state.name) {
					case "text": {
						const $ = property.properties.type.state.node;
						icons += $.properties.is_valid.switch({
							no: "🔴",
							yes: "",
						});
						icons += $.properties.is_editable.switch({
							no: "",
							yes: ($) =>
								$.properties.modified.switch({
									yes: "🔵",
									no: "",
								}),
						});

						const value = $.properties.is_set.switch<string | undefined>({
							no: "",
							yes: ($) => '"' + $.properties.text + '"',
						});
						status.push(value);

						const registration = asRegisterable($);
						const refcount = registration.__references?.value ?? "-";
						const subcount = registration.__subscriptions?.value ?? "-";
						status.push(`${refcount}/${subcount}`);

						$.properties.navigable.switch({
							no: undefined,
							yes: ($) => {
								icons += $.properties.resolve_status.properties.status.switch({
									"failed to resolve": "❗️",
									"is busy resolving": "…",
									resolved: "",
									unresolved: "❕",
								});
								status.push($.properties.resolve_status.properties.status.state.name);
							},
						});

						obj = iText($);
						break;
					}
					case "number": {
						const $ = property.properties.type.state.node;
						icons += $.properties.is_valid.switch({
							no: "🔴",
							yes: "",
						});
						icons += $.properties.is_editable.switch({
							no: "",
							yes: ($) =>
								$.properties.modified.switch({
									yes: "🔵",
									no: "",
								}),
						});

						const value = $.properties.is_set.switch<string | undefined>({
							no: "-",
							yes: ($) => $.properties.number.value + "",
						});
						status.push(value);

						const registration = asRegisterable($);
						const refcount = registration.__references?.value ?? "-";
						const subcount = registration.__subscriptions?.value ?? "-";
						status.push(`${refcount}/${subcount}`);

						obj = $;
						break;
					}
					case "file": {
						const $ = property.properties.type.state.node;
						icons += $.properties.is_valid.switch({
							no: "🔴",
							yes: "",
						});
						icons += $.properties.is_editable.switch({
							no: "",
							yes: ($) =>
								$.properties.modified.switch({
									yes: "🔵",
									no: "",
								}),
						});

						const registration = asRegisterable($);
						const refcount = registration.__references?.value ?? "-";
						const subcount = registration.__subscriptions?.value ?? "-";
						status.push(`${refcount}/${subcount}`);

						obj = $;
						break;
					}
					case "group": {
						const $ = property.properties.type.state.node;
						const node_status = nodeStatus($.properties.node);
						icons += node_status;

						const registration = asRegisterable($);
						const refcount = registration.__references?.value ?? "-";
						const subcount = registration.__subscriptions?.value ?? "-";
						status.push(`${refcount}/${subcount}`);

						obj = generateNodeReport($.properties.node);
						child_entries += obj["child entries"] || 0;
						query_entries += obj["query entries"] || 0;
						break;
					}
					case "state group": {
						const $ = property.properties.type.state.node;
						icons += $.properties.is_valid.switch({
							no: "🔴",
							yes: "",
						});
						icons += $.properties.is_modified.switch({
							no: "",
							yes: "🔵",
						});

						const value = $.properties.is_set.switch({
							no: "-",
							yes: ($) => "'" + $.properties.state.entry + "'",
						});
						status.push(value);

						const registration = asRegisterable($);
						const refcount = registration.__references?.value ?? "-";
						const subcount = registration.__subscriptions?.value ?? "-";
						status.push(`${refcount}/${subcount}`);

						const states: any = {};
						for (const [state_name, state] of $.properties.states) {
							const state_location = state.properties.location.state.name;
							const node_status = nodeStatus(state.properties.node);
							const state_line = state.properties.is_selected.switch({
								no: `'${state_name}'`,
								yes: `🔘${node_status}'${state_name}' (${state_location})`,
							});
							states[state_line] = generateNodeReport(state.properties.node);
							child_entries += state.properties.is_selected.switch({
								no: 0,
								yes: states[state_line]["child entries"] || 0,
							});
							query_entries += states[state_line]["query entries"] || 0;
						}
						obj["states"] = states;
						obj["_$"] = iStategroup($);
						break;
					}
					case "collection": {
						const $ = property.properties.type.state.node;
						icons += $.properties.is_valid.switch({
							no: "🔴",
							yes: "",
						});
						icons += $.properties.modified.switch({
							no: "",
							yes: "🔵",
						});

						let col_children = 0;
						let col_query_entries = 0;

						const entries: any = {};
						for (const [internal_key, entry] of $.properties.entries) {
							const entry_location = entry.properties.location.state.name;
							const entry_key = entry.properties.key.ref?.properties.is_set.switch({
								no: () => "",
								yes: ($) => $.properties.text,
							});
							let node_status = nodeStatus(entry.properties.node);
							const deleted = entry.properties.location.switch({
								local: "",
								remote: ($) =>
									$.properties.marked_for_deletion.switch({
										no: "",
										yes: () => {
											node_status += "❌";
											return ", deleted";
										},
									}),
							});
							const entry_line = `${node_status}"${entry_key}" (${entry_location}${deleted}, [${internal_key}])`;
							entries[entry_line] = generateNodeReport(entry.properties.node);
							col_children += 1 + entries[entry_line]["child entries"] || 0;
							col_query_entries += entries[entry_line]["query entries"] || 0;
						}
						obj["entries"] = entries;

						const registrations = getCollectionRegistrations($);
						const entries_reg = asRegisterable(registrations.entries);
						const entries_refcount = entries_reg.__references?.value ?? "-";
						const entries_subcount = entries_reg.__subscriptions?.value ?? "-";
						const updates_reg = asRegisterable(registrations.updates);
						const updates_refcount = updates_reg.__references?.value ?? "-";
						const updates_subcount = updates_reg.__subscriptions?.value ?? "-";
						const key_regs: any = {};
						for (const [key, reg] of registrations.keys) {
							const key_reg = asRegisterable(reg);
							const key_refcount = key_reg.__references?.value ?? "-";
							const key_subcount = key_reg.__subscriptions?.value ?? "-";
							key_regs[key] = `${key_refcount}/${key_subcount}`;
						}
						const reg_obj: any = {};
						reg_obj["entries"] = `${entries_refcount}/${entries_subcount}`;
						reg_obj["updates"] = `${updates_refcount}/${updates_subcount}`;
						reg_obj[`keys (${registrations.keys.size})`] = key_regs;
						obj[`registrations`] = reg_obj;
						obj["child entries"] = col_children;
						obj["query entries"] = col_query_entries;
						child_entries += col_children;
						query_entries += col_query_entries;

						obj["_$"] = iCollection($);
						break;
					}
				}
				const kind = property.inferences.property_type().properties.type.state.node.output.value_source().switch({
					derived: "derived",
					base: "",
				});
				status.push(kind);
				break;
			}
		}
		// Filter out all falsy values and join them
		status = status.filter(Boolean).join(", ");
		if (status !== "") {
			status = " (" + status + ")";
		}
		attributes[`${icons}'${attribute_name}'${status}`] = obj;
	}
	const queries: any = {};
	for (const [query_name, query] of node.properties.queries) {
		has_queries = true;
		const query_obj: any = {};
		const entries: any = {};
		switch (query.properties.query.properties.has_result.state.name) {
			case "yes": {
				const $ = query.properties.query.properties.has_result.state.node;
				for (const [internal_key, entry] of $.properties.entries) {
					const key = getQueryEntryIndex(entry.properties.entry_status);
					const is_valid = entry.properties.is_valid.switch({
						no: "🔴",
						yes: "",
					});
					const location = entry.properties.entry_status.properties.initialized.switch({
						no: "remote",
						yes: "local",
						unknown: "unknown",
					});
					const line = `${is_valid}"${key}" (${location}, [${internal_key}])`;
					const cells: any = {};
					for (const [cell_name, cell] of entry.properties.cells) {
						let value: any;
						switch (cell.properties.type.state.name) {
							case "text": {
								const $ = cell.properties.type.state.node;
								value = $.properties.is_set.switch({
									no: undefined,
									yes: ($) => $.properties.text as any,
								});
								break;
							}
							case "number": {
								const $ = cell.properties.type.state.node;
								value = {
									value: $.properties.is_set.switch({
										no: undefined,
										yes: ($) => $.properties.number.value as any,
									}),
								};
								$.properties.numerical_type_properties.properties.initialized.switch({
									no: undefined,
									yes: ($) => {
										value.decimals = $.properties.decimals.value;
										value.unit = $.properties.unit;
									},
								});
								break;
							}
							case "file": {
								const $ = cell.properties.type.state.node;
								value = $.properties.is_set.switch({
									no: undefined,
									yes: ($) => ($.properties.token + $.properties.extension) as any,
								});
								break;
							}
							case "state group": {
								const $ = cell.properties.type.state.node;
								value = $.properties.is_set.switch({
									no: undefined,
									yes: ($) => $.properties.state as any,
								});
								break;
							}
							case "widget":
								break;
						}
						cells["'" + cell_name + "'"] = value;
					}
					entries[line] = { cells: cells, _$: entry };
				}
				break;
			}
			case "no":
				break;
		}
		const amount_of_entries = Object.keys(entries).length;
		query_entries += amount_of_entries;
		query_obj["entries"] = entries;
		query_obj["size"] = amount_of_entries;
		query_obj["_$"] = query.properties.query;
		queries[query_name] = query_obj;
	}
	if (has_attributes) {
		report["attributes"] = attributes;
		report["child entries"] = child_entries;
	}
	if (has_queries) {
		report["queries"] = queries;
		report["query entries"] = query_entries;
	}
	report["_$"] = node;
	return report;
}

export function generateClientStateReport(): any {
	const report: any = {};
	const node_status = nodeStatus(client_state.properties.root);
	report[` ${node_status} root`] = generateNodeReport(client_state.properties.root);
	report["applying defaults"] = applyingDefaults;
	report["resolving references"] = resolvingReferences;
	report["pending subscriptions"] = pendingSubscriptions;
	report["subscription count"] = DatastoreProxy.instance.subscription_counter;
	report["query count"] = DatastoreProxy.instance.query_counter;
	report["permission query count"] = DatastoreProxy.instance.subscribeOnce_counter;
	report["version"] = client_state.properties.version;
	report["_$"] = client_state;
	return report;
}

(<any>window).getClientStateReport = (node?: api_cs.Cnode) => {
	if (!node) {
		return generateClientStateReport();
	} else {
		return generateNodeReport(node);
	}
};

(<any>window).shhht_supersecret_devserver_command_generator = () => {
	const url = window.location.href.split("#")[0];
	const token = JSON.parse(window.localStorage.KAUTH)["has session"][1].token;
	const uname = client_state.properties.session.properties.status.switch({
		authenticated: ($) =>
			$.properties.anonymous_session.switch({
				no: ($) => $.properties.username,
				yes: () => "",
			}),
		"not authenticated": () => "",
	});
	return `node build/dev-server/index.js '${url}' --token '${token}' --uname '${uname}' --local-engine --open-browser`;
};

(<any>window).readablePath = (
	thing:
		| api_cs.Ctext__type__property
		| api_cs.Cnumber__type__property
		| api_cs.Cfile__type__property
		| api_cs.Cgroup__type__property
		| api_cs.Cstate_group__type__property
		| api_cs.Ccollection
		| api_cs.Cnode
): string => {
	let path = "";
	if (!(thing instanceof api_cs.Cnode)) {
		path = `.'${thing.parent.parent.key_value}'`;
	}
	let node = thing.component_root;
	let position: api_cs.Cnode_position | undefined = node.input.position();
	while (position) {
		switch (position.variant.name) {
			case "entity": {
				const entity = position.variant.definition.variant;
				switch (entity.name) {
					case "root":
						return path;
					case "entry": {
						const entry = entity.definition;
						const key = entry.properties.key.ref.properties.is_set.switch({
							no: () => entry.key.split("-")[0],
							yes: ($) => $.properties.text,
						});
						node = entry.component_root;
						path = `.'${entry.parent.parent.parent.key_value}'['${key}']${path}`;
						break;
					}
				}
				break;
			}
			case "group": {
				const group = position.variant.definition;
				node = group.component_root;
				path = `.'${group.parent.parent.key_value}'${path}`;
				break;
			}
			case "root":
				return path;
			case "state": {
				const state = position.variant.definition;
				node = state.component_root;
				path = `'${state.parent.parent.parent.key_value}'?'${state.key_value}'${path}`;
				break;
			}
		}
		position = node.input.position();
	}
	return path;
};
